// Blending one piece into another.
//
// The tier a param declares was written to answer "what does changing this
// cost?" — structural means a rebuild, continuous means live at 60fps. It
// turns out to answer "can this be interpolated?" as well, because they are
// the same question: a continuous param is one built to move smoothly, and an
// enum or a seed has no midpoint to move through.
//
// So this reads the same descriptors the panel renders from. A param added to
// a schema becomes tweenable the moment it is declared, with no edit here.
//
//   continuous, cosmetic  lerped
//   structural            lerped on a stateless substrate, switched on a
//                         stateful one — a rebuild re-seeds a simulation, and
//                         doing that every frame destroys it
//   enum, text, seed      switched at the midpoint
//
// Inputs must already be normalised: blending is per-frame work and should not
// be re-deriving defaults each time.

import { fields } from './fields/index.js'
import { substrates } from './substrates/index.js'
import { motions, motionParams } from './motion/index.js'
import { markParams, inkParams, frameParams } from './marks.js'
import { clockParams } from './clock.js'

const lerp = (a, b, u) => a + (b - a) * u

// Linear in and out of a morph reads as a jolt at both ends; this does not.
export const smoothstep = (u) => (u <= 0 ? 0 : u >= 1 ? 1 : u * u * (3 - 2 * u))

// Where a morph has got to. Playing once, it arrives and stays. Looping, it
// walks back — a triangle rather than a sawtooth, because a sawtooth would
// snap from the target to the start every cycle, which is the one thing a
// graceful transition must not do. `ms` is the time for one leg, so a round
// trip takes twice that.
export function morphPhase(elapsedMs, ms, loop) {
  const x = Math.max(0, elapsedMs) / Math.max(1, ms)
  return smoothstep(loop ? 1 - Math.abs(1 - (x % 2)) : Math.min(1, x))
}

const TRANSFORM = [
  { key: 'scale', tier: 'continuous' }, { key: 'x', tier: 'continuous' },
  { key: 'y', tier: 'continuous' }, { key: 'rotation', tier: 'continuous' },
  { key: 'spin', tier: 'continuous' },
]

function blend(params, a, b, u, holdStructural) {
  const out = {}
  for (const p of params) {
    const av = a[p.key], bv = b[p.key]
    if (av === bv) { out[p.key] = av; continue }
    // Nothing between 'grid' and 'polar', or between two glyph sets.
    if (p.type === 'enum' || p.type === 'text') { out[p.key] = u < 0.5 ? av : bv; continue }
    if (p.tier === 'structural' && holdStructural) { out[p.key] = u < 0.5 ? av : bv; continue }
    // A circular param takes the short way round: 350 to 10 is twenty degrees
    // forward, not three hundred and forty back through the whole wheel.
    if (p.wrap) {
      const w = p.wrap
      let d = (((bv - av) % w) + w) % w
      if (d > w / 2) d -= w
      const h = (((av + d * u) % w) + w) % w
      out[p.key] = p.step >= 1 ? Math.round(h) % w : h
      continue
    }
    const v = lerp(av, bv, u)
    // A count is a count: step 1 or coarser means the value is discrete.
    out[p.key] = p.step >= 1 ? Math.round(v) : v
  }
  return out
}

// A layer present on only one side fades rather than appears: the same layer
// with no ink, so the ordinary lerp carries it in or out.
const ghost = (layer) => ({ ...layer, ink: { ...layer.ink, gain: 0 } })

function blendLayer(a, b, u) {
  const fieldMod = (u < 0.5 ? fields[a.field.type] : fields[b.field.type]) || fields.interference
  const subMod = (u < 0.5 ? substrates[a.substrate.type] : substrates[b.substrate.type]) || substrates.grid
  // Rebuilding a stateful substrate throws its simulation away, so its
  // structural params wait for the midpoint instead of stepping every frame.
  const stateful = motions[subMod.motion] && motions[subMod.motion].stateful
  const sameField = a.field.type === b.field.type
  const sameSub = a.substrate.type === b.substrate.type

  return {
    enabled: u < 0.5 ? a.enabled : b.enabled,
    timeScale: lerp(a.timeScale, b.timeScale, u),
    phaseOffset: lerp(a.phaseOffset, b.phaseOffset, u),
    field: {
      type: fieldMod.id,
      // Across a type change there is no shared schema to blend through, so
      // the side that owns the type owns its params too.
      ...(sameField ? blend(fieldMod.params, a.field, b.field, u, stateful)
        : (u < 0.5 ? a.field : b.field)),
    },
    substrate: {
      type: subMod.id,
      ...(sameSub ? blend(subMod.params, a.substrate, b.substrate, u, stateful)
        : (u < 0.5 ? a.substrate : b.substrate)),
    },
    motion: blend(motionParams, a.motion, b.motion, u, stateful),
    mark: blend(markParams, a.mark, b.mark, u, stateful),
    ink: blend(inkParams, a.ink, b.ink, u, stateful),
    transform: blend(TRANSFORM, a.transform, b.transform, u, stateful),
  }
}

export function tween(a, b, u) {
  if (u <= 0) return a
  if (u >= 1) return b
  const n = Math.max(a.layers.length, b.layers.length)
  const layers = []
  for (let i = 0; i < n; i++) {
    const la = a.layers[i] || ghost(b.layers[i])
    const lb = b.layers[i] || ghost(a.layers[i])
    layers.push(blendLayer(la, lb, u))
  }
  return {
    seed: u < 0.5 ? a.seed : b.seed,
    clock: blend(clockParams, a.clock, b.clock, u, false),
    frame: blend(frameParams, a.frame, b.frame, u, false),
    layers,
  }
}
